import { useOrganization, useUser } from '@clerk/clerk-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ArtworkApiError } from '../services/apiClient';
import { ConfirmAlert } from '../ui/confirm';
import { usableBrands, type BrandCard } from '../services/work';
import { Button, IconButton, SegmentedControl, Spinner } from '../ui/controls';
import { tip } from '../ui/tooltip';
import { Icon } from '../editor/chrome/icons';
import { wholeOpenWith } from '../editor/wholeMenu';
import type { MenuEntry } from '../ui/menu';
import { archiveFile, createFile, deleteFile, duplicateFile, listProjectSummaries, moveFile } from './api';
import { ProjectPicker, useProjects, type ProjectCard, type ProjectDetail } from '@flobi/asset-library/projects';
import { forgetSummaries, listVisibleProjects } from '../projects/projectsApi';
import { AllProjects, ProjectPage, type FileSort } from '../projects/ProjectsViews';
import { openFileShare } from '../share/artworkShare';
import { Notifications } from './Notifications';
import { brandInForce, Composer, NewFileButton, useStartBrand, type Start } from './Composer';
import { FileCard, FileGrid, PlaceholderGrid } from './FileCard';
import { FirstVisit, useWelcomed } from './FirstVisit';
import { ArtworkGetStarted, ArtworkHomeMark } from '../lib/tours/ArtworkGetStarted';
import { ErrorLine, Nothing, PlaceholderCard, SectionHead, ShowMore, Title } from './parts';
import { PlaceholderChips, ProjectChips } from './ProjectChips';
import { PutAwayPage, type PutAwayKind } from './PutAway';
import { namedSummaries, type FileRow, type ProjectInfo, type ProjectSummary, type Scope } from './rows';
import { Search } from './Search';
import { useFileList } from './useFileList';
import { cachedBrands, cachedInfos, cachedSummaries } from './homeCache';
import { WorkspaceMenu } from './WorkspaceMenu';
import { differsFromFactory, readNewFileDefaults, type NewFileDefaults } from './newFileDefaults';
import { PhoneEmptyState, usePhone } from './PhoneEmptyState';
import { QUICK_SIZES, shortName, startSizeOf } from './sizes';

type Below = Exclude<Scope, 'recent'>;

/** How many recent files stand in a row while they load. */
const SHELF = 4;
/** How many recent files the Home shows: two rows, so the page is your work and not a gap. */
const RECENT = 8;
/** How many projects the Home's row shows before "See All". */
const PROJECT_ROW = 8;
/** The sizes a phone's empty list offers as ways to start: a post, a story, a slide. */
const PHONE_STARTS = [
  { id: 'post', icon: 'image' },
  { id: 'story', icon: 'image' },
  { id: 'slide', icon: 'board' },
] as const;

/**
 * Artwork's home (Tarek's pick A2, 2026-09-30): the prompt box, "What are we creating
 * today?", whose Create makes a blank page when it has no words; then your recent files; then your
 * projects as a shelf of photo pockets (names only, no colours; the projects the shared kit lists
 * for Artwork). Everything else is a step away and in the address, so back and forward work: every
 * file (`/?view=files`, whose files by a segmented control), every project (`/?view=projects`) and
 * a project (`/?project=<id>`), both drawn as All Files is (`projects/ProjectsViews.tsx`), and my
 * Trash (`/?view=trash`) and Archived (`/?view=archived`), reached from All Files (`PutAway.tsx`).
 * Search replaces the Home with one grid.
 *
 * A new file started on a project's page is that project's; anywhere else it is in no project.
 */
export function Home() {
  const { organization } = useOrganization();
  const org = organization?.id ?? '';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project');
  const allProjects = !projectId && params.get('view') === 'projects';
  const allFiles = !projectId && params.get('view') === 'files';
  const putAway = !projectId && (params.get('view') === 'trash' || params.get('view') === 'archived') ? (params.get('view') as PutAwayKind) : null;

  const [scope, setScope] = useState<Below>('mine');
  const belowBox = useRef<HTMLElement>(null);
  /** The tallest the Owned / Shared / Workspace section has been since the page opened. */
  const [hold, setHold] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  // Typing waits for a pause; clearing is at once.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), search.trim() ? 250 : 0);
    return () => clearTimeout(timer);
  }, [search]);

  const recent = useFileList({ scope: 'recent' }, `${org}|recent`, RECENT);
  // Every file, whose by the segmented control, is its own page now: asked for only there.
  // The key says whether the page is open at all: it was the same with All Files shut, so
  // opening All Files asked for nothing until a tab or a reload (QA 2026-10-05, ART-HOM-15).
  const below = useFileList(allFiles ? { scope } : null, allFiles ? `${org}|${scope}` : `${org}|closed`, undefined, { keep: true });
  const found = useFileList(query ? { search: query } : null, `${org}|search|${query}`);
  const inProject = useFileList(projectId ? { projectId } : null, `${org}|project|${projectId ?? ''}`);
  /** Whether the workspace has any file at all: an empty one keeps the first page. */
  // Any file at all, in a project or not: 'workspace' alone is files in NO project, and a
  // workspace whose files were all filed away read as empty, so the first page never left.
  const probe = useFileList({}, `${org}|probe`, 1);

  const projectsKit = useProjects();
  /** Projects this person can see that are shown in Artwork, for the names on file cards and the Projects row. */
  // Each starts from what this workspace showed last (homeCache.ts), so coming back from a file
  // draws the page at once; the answers below replace it.
  const [infos, setInfos] = useState<Map<string, ProjectInfo> | null>(() => cachedInfos.get(org) ?? null);
  /** Each project's Artwork file count and newest pictures (`artwork.doc.projects`). */
  const [summaries, setSummaries] = useState<ProjectSummary[] | null>(() => cachedSummaries.get(org) ?? null);
  const projectsVersion = projectsKit.version;
  useEffect(() => {
    let live = true;
    listVisibleProjects()
      .then((map) => {
        cachedInfos.set(org, map);
        if (live) setInfos(map);
      })
      .catch((err: Error) => {
        if (import.meta.env.DEV) console.warn('[home] project.list.forApp:', err.message);
        if (live) setInfos(new Map());
      });
    listProjectSummaries()
      .then((list) => {
        cachedSummaries.set(org, list);
        if (live) setSummaries(list);
      })
      .catch((err: ArtworkApiError) => {
        // Project navigation remains available even if Artwork counts cannot be loaded.
        if (import.meta.env.DEV) console.warn('[home] artwork.doc.projects:', err.message);
        if (live) setSummaries(null);
      });
    return () => {
      live = false;
    };
  }, [org, projectsVersion]);
  const named = useMemo(() => (infos ? namedSummaries(summaries, infos) : null), [infos, summaries]);
  /** A file was made, moved, copied or deleted: the project cards' counts and pictures are read again. */
  const projectsChanged = useCallback(() => {
    forgetSummaries();
    projectsKit.bump();
  }, [projectsKit]);

  /** The brands the prompt box can make a file in; asked again for each workspace. */
  const [brands, setBrands] = useState<BrandCard[] | null>(() => cachedBrands.get(org) ?? null);
  /** The brand new files start in, shared by the prompt box and New File. */
  const [brandId, setBrandId] = useStartBrand(org);
  const startBrand = brandInForce(brands, brandId)?.id;
  useEffect(() => {
    let live = true;
    setBrands(cachedBrands.get(org) ?? null);
    usableBrands()
      .then((list) => {
        cachedBrands.set(org, list);
        if (live) setBrands(list);
      })
      .catch((err: Error) => {
        // No brands to offer is not a reason to keep the box from working.
        if (import.meta.env.DEV) console.warn('[home] artwork.brand.list:', err.message);
        if (live) setBrands([]);
      });
    return () => {
      live = false;
    };
  }, [org]);

  /** How a project's files are ordered on its page. */
  const [fileSort, setFileSort] = useState<FileSort>('edited');

  const projectOf = (id: string | null) => (id && infos ? (infos.get(id) ?? null) : null);

  // ── what can be done to files ──────────────────────────────────────────────

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /** A phone draws its empty lists as the suite's (pick E); a laptop keeps Artwork's own lines. */
  const phone = usePhone();
  const [doomed, setDoomed] = useState<FileRow | null>(null);
  const { welcomed, welcome } = useWelcomed();

  /**
   * Whether this page is still the one showing. A new file arriving after the person had
   * already opened another pulled them out of it into the blank one (files-doc-F19).
   */
  const showing = useRef(true);
  useEffect(() => {
    showing.current = true;
    return () => {
      showing.current = false;
    };
  }, []);

  /** Opens a file, handing the editor the name the tile shows so its title never reads "Untitled" first (files-doc-F25). */
  const open = (file: { id: string; name: string }, startWith?: NewFileDefaults) => navigate(`/f/${file.id}`, { state: { name: file.name, ...(startWith ? { startWith } : {}) } });

  /** A new file: blank, or as the prompt box or a size under it describes, in a project when one is open. */
  const create = async (start: Start | null = null, inside: string | null = null) => {
    setBusy(true);
    setFailure(null);
    try {
      const file = await createFile({ projectId: inside, ...(start ? { name: start.name, artboard: start.artboard, brandId: start.brandId } : {}) });
      welcome();
      if (inside) projectsChanged();
      // A file just made starts with what this person likes new files to start with (Settings › Artwork).
      const defaults = readNewFileDefaults();
      if (showing.current) open(file, differsFromFactory(defaults) ? defaults : undefined);
    } catch (err) {
      if (showing.current) setFailure((err as ArtworkApiError).message);
    } finally {
      if (showing.current) setBusy(false);
    }
  };

  /** A phone's ways to start on an empty list: a file at one of three common sizes, here. */
  const phoneStarts = (inside: string | null = null) =>
    PHONE_STARTS.map(({ id, icon }) => {
      const preset = QUICK_SIZES.find((one) => one.id === id)!;
      return {
        id,
        icon,
        label: shortName(preset),
        hint: preset.says,
        disabled: busy,
        onClick: () => void create({ name: 'Untitled', artboard: startSizeOf(preset), ...(!inside && startBrand ? { brandId: startBrand } : {}) }, inside),
      };
    });

  const everyList = [recent, below, found, inProject];

  const move = async (file: FileRow, to: string | null) => {
    if (file.projectId === to) return;
    setFailure(null);
    try {
      const moved = (await moveFile(file.id, to)) ?? { ...file, projectId: to };
      for (const list of everyList) list.patch(file.id, list === inProject && moved.projectId !== projectId ? null : moved);
      projectsChanged();
    } catch (err) {
      setFailure((err as ArtworkApiError).message);
    }
  };

  /**
   * Move to Project…: the shared picker (projects this person can add to that are shown in
   * Artwork, a search, New project…, No project while the file is in one), hung where the menu
   * row was; then the kit's question when the move changes who can see the file (Decision 5).
   */
  const [moving, setMoving] = useState<{ file: FileRow; at: { x: number; y: number } } | null>(null);
  const lastPress = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const note = (event: PointerEvent) => {
      lastPress.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener('pointerdown', note, true);
    return () => window.removeEventListener('pointerdown', note, true);
  }, []);
  const moveTo = async (file: FileRow, target: Pick<ProjectCard | ProjectDetail, 'id' | 'name'> | null) => {
    const to = target?.id ?? null;
    if (file.projectId === to) return;
    let ok: boolean;
    try {
      ok = await projectsKit.confirmMove({
        projectId: to,
        fromProjectId: file.projectId,
        itemOwnerId: file.owner?.id ?? null,
        // A file has no people of its own: its people are its project's.
        itemPeople: [],
        itemName: file.name,
        targetName: target?.name,
        noun: 'file',
      });
    } catch (err) {
      setFailure((err as { userMessage?: string })?.userMessage || 'Couldn’t check who can see it. Try again.');
      return;
    }
    if (ok) await move(file, to);
  };

  /** Into the Trash, or archived: either way out of every list here. */
  const remove = async (file: FileRow, how: 'trash' | 'archive' = 'trash') => {
    setDoomed(null);
    setFailure(null);
    try {
      await (how === 'trash' ? deleteFile(file.id) : archiveFile(file.id, true));
      for (const list of everyList) list.patch(file.id, null);
      // Recents is a shelf of four: the next one steps up rather than leaving a gap.
      recent.reload();
      if (file.projectId) projectsChanged();
    } catch (err) {
      setFailure((err as ArtworkApiError).message);
    }
  };

  /**
   * The file being copied now, said on the page while it is: a second press made a second
   * copy, with nothing to say the first was coming (review H5).
   */
  const [copying, setCopying] = useState<string | null>(null);
  /** A copy of a whole file, which then heads Recent and the list it was copied in (ART-UX-06). */
  const duplicate = async (file: FileRow) => {
    if (copying) return;
    setFailure(null);
    setCopying(file.name);
    try {
      await duplicateFile(file.id);
      recent.reload();
      if (allFiles) below.reload();
      if (projectId) inProject.reload();
      if (query) found.reload();
      if (file.projectId) projectsChanged();
    } catch (err) {
      setFailure((err as ArtworkApiError).message);
    } finally {
      setCopying(null);
    }
  };

  const fileMenu = (file: FileRow) => (): MenuEntry[] => [
    { id: 'open', label: 'Open', onSelect: () => open(file) },
    { id: 'tab', label: 'Open in New Tab', onSelect: () => window.open(`/f/${file.id}`, '_blank', 'noopener') },
    { id: 'duplicate', label: 'Duplicate', icon: 'copy', onSelect: () => void duplicate(file) },
    { kind: 'separator' },
    // Its people are its project's; a file in no project is offered Move to Project in the popup.
    { id: 'share', label: 'Share…', onSelect: () => openFileShare({ id: file.id, name: file.name }) },
    { id: 'move', label: 'Move to Project…', onSelect: () => setMoving({ file, at: lastPress.current ?? { x: window.innerWidth / 2, y: 72 } }) },
    ...wholeOpenWith({ documentId: file.id, name: file.name, onError: setFailure }),
    { kind: 'separator' },
    // Archive, like Move to Trash, is the maker's (artwork-back says so to anybody else).
    ...(file.ownedByMe === false ? [] : [{ id: 'archive', label: 'Archive', onSelect: () => void remove(file, 'archive') }]),
    { id: 'delete', label: 'Move to Trash…', icon: 'trash', danger: true, onSelect: () => setDoomed(file) },
  ];

  const card = (file: FileRow, options: { big?: boolean; recents?: boolean; showProject?: boolean } = {}) => (
    <FileCard
      file={file}
      big={options.big}
      project={options.showProject === false ? null : projectOf(file.projectId)}
      when={options.recents ? (file.myLastEditAt ?? file.updatedAt) : file.updatedAt}
      onOpen={() => open(file)}
      menu={fileMenu(file)}
    />
  );

  // ── where we are ───────────────────────────────────────────────────────────

  const openProject = (id: string) => setParams({ project: id });
  const seeAllProjects = () => setParams({ view: 'projects' });
  const seeAllFiles = () => setParams({ view: 'files' });
  const seePutAway = (view: PutAwayKind) => setParams({ view });
  // A fixed page, never the previous history entry: a project's "Projects" link opens All Projects, whose Back then
  // returned to that project, and round again with no way home (Pulse #58). Trash and Archived are reached from All Files.
  const back = () => navigate(putAway ? '/?view=files' : '/', { replace: true });
  // Another workspace (the account menu's switcher takes the page to Home): its files, not the last search.
  useEffect(() => setSearch(''), [org]);

  // A new view starts at its top.
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [projectId, allProjects, allFiles, putAway]);

  const workspaceHasFiles = !!probe.rows && probe.rows.length > 0;
  const workspaceEmpty = !!probe.rows && probe.rows.length === 0;
  const workspaceHasProjects = !!infos?.size;
  const atRoot = !projectId && !allProjects && !allFiles && !putAway;
  const firstVisit = atRoot && !query && !workspaceHasProjects && (workspaceEmpty || !welcomed);

  const error = failure ?? (query ? found.error : projectId ? inProject.error : allProjects ? null : allFiles ? below.error : recent.error);
  const goneProject = () => navigate('/?view=projects');

  const picker = moving ? (
    <ProjectPicker
      open
      onOpenChange={(next) => !next && setMoving(null)}
      title={`Move “${moving.file.name}” to`}
      // Its own project is listed first, ticked, and found by a search (it was left out, so a
      // search for it found nothing); choosing it changes nothing.
      currentProjectId={moving.file.projectId}
      allowNone
      align="start"
      onPick={(target) => void moveTo(moving.file, target)}
      trigger={<span aria-hidden="true" className="pointer-events-none fixed block size-0" style={{ left: moving.at.x, top: moving.at.y }} />}
    />
  ) : null;

  let page: ReactNode;
  if (firstVisit) {
    return (
      <div className="h-dvh overflow-hidden bg-page text-label">
        <FirstVisit
          busy={busy}
          onNewFile={() => {
            // Making a file is moving on: without this, coming back from it opened the
            // first page again, every time.
            welcome();
            void create();
          }}
          onShowFiles={workspaceHasFiles || probe.error ? welcome : undefined}
          corner={
            <div className="flex items-center gap-[8px]">
              <Notifications />
              <WorkspaceMenu />
            </div>
          }
        />
        {failure ? <ErrorLine className="fixed inset-x-0 bottom-[24px] text-center">{failure}</ErrorLine> : null}
        {/* The first page is Home too: the lessons card opens by itself here (lib/tours). */}
        <ArtworkHomeMark />
      </div>
    );
  }

  if (projectId) {
    const loaded = inProject.rows;
    const rows = loaded && fileSort === 'name' ? [...loaded].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })) : loaded;
    const newest = loaded?.reduce<string | null>((at, file) => (!at || file.updatedAt > at ? file.updatedAt : at), null) ?? null;
    page = (
      <ProjectPage
        key={projectId}
        projectId={projectId}
        onAll={seeAllProjects}
        onGone={goneProject}
        newest={newest}
        sort={fileSort}
        onSort={setFileSort}
        // Started here, a file is this project's, in the project's brand.
        newFile={<NewFileButton busy={busy} onStart={(start) => void create(start, projectId)} />}
      >
        {error && !(phone && !failure && rows === null) ? <ErrorLine>{error}</ErrorLine> : null}
        {rows === null ? (
          inProject.error ? (
            phone ? <PhoneEmptyState tone="error" title="Couldn’t load this project’s files" onRetry={inProject.reload} /> : null
          ) : (
            <PlaceholderGrid />
          )
        ) : rows.length === 0 && phone ? (
          <PhoneEmptyState title="Nothing in this project yet" line="Files you make here belong to this project." starts={phoneStarts(projectId)} />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-[12px] py-[48px]">
            <p className="text-center text-[13px] text-label-2">No files yet</p>
            <Button onClick={() => void create(null, projectId)} disabled={busy}>
              New File
            </Button>
          </div>
        ) : (
          <FileGrid>
            {rows.map((file) => (
              <li key={file.id}>{card(file, { showProject: false })}</li>
            ))}
          </FileGrid>
        )}
        <ShowMore list={inProject} />
      </ProjectPage>
    );
  } else if (allProjects) {
    page = <AllProjects onBack={back} onOpen={openProject} />;
  } else if (putAway) {
    page = (
      <PutAwayPage
        key={putAway}
        kind={putAway}
        org={org}
        onBack={back}
        onOpen={open}
        projectOf={projectOf}
        onChanged={(file) => {
          recent.reload();
          if (file.projectId) projectsChanged();
        }}
      />
    );
  } else if (allFiles) {
    page = (
      <>
        <header className="mb-[8px] flex h-[72px] items-center gap-[12px]">
          <IconButton icon="back" label="Back" size={30} onClick={back} />
          <div className="min-w-0 flex-1">
            <Title count={below.rows && !below.stale ? `${below.rows.length}${below.more ? '+' : ''}` : undefined}>All Files</Title>
          </div>
          <NewFileButton busy={busy} brandId={startBrand} onStart={(start) => void create(start)} />
        </header>
        {error && !(phone && !failure && below.rows === null) ? <ErrorLine>{error}</ErrorLine> : null}
        <section ref={belowBox} style={hold ? { minHeight: hold } : undefined}>
          <div className="mb-[16px] flex items-center gap-[12px]">
            <SegmentedControl
              label="Whose files"
              className="w-full max-w-[380px]"
              value={scope}
              onChange={(next) => {
                // The section keeps the height it had, so a shorter list (Shared with Me
                // is often empty) leaves room at the bottom instead of pulling the page
                // up under the pointer.
                setHold((was) => Math.max(was, belowBox.current?.offsetHeight ?? 0));
                setScope(next);
              }}
              segments={[
                { value: 'mine', label: 'Owned by Me' },
                { value: 'shared', label: 'Shared with Me' },
                { value: 'workspace', label: 'Workspace' },
              ]}
            />
            <span className="flex-1" />
            {(['archived', 'trash'] as const).map((view) => (
              <button key={view} type="button" onClick={() => seePutAway(view)} className="shrink-0 text-[13px] text-accent-text hover:underline">
                {view === 'trash' ? 'Trash' : 'Archived'}
              </button>
            ))}
          </div>
          {below.rows === null ? (
            below.error ? (
              phone ? <PhoneEmptyState tone="error" title="Couldn’t load your files" onRetry={below.reload} /> : null
            ) : (
              <PlaceholderGrid />
            )
          ) : below.rows.length === 0 && phone ? (
            <PhoneEmptyState {...ALL_FILES_EMPTY[scope]} starts={scope === 'shared' ? undefined : phoneStarts()} />
          ) : below.rows.length === 0 ? (
            <Nothing />
          ) : (
            <div className={`transition-opacity duration-150 ${below.stale ? 'opacity-50' : ''}`}>
              <FileGrid>
                {below.rows.map((file) => (
                  <li key={file.id}>{card(file)}</li>
                ))}
              </FileGrid>
            </div>
          )}
          {below.stale ? null : <ShowMore list={below} />}
        </section>
      </>
    );
  } else {
    // No Pinned row on the Home (Farah, 2026-10-23: "no need for pinned projects here"): pinned ones sit in Projects like the rest.
    const rowOfProjects = named ? named.slice(0, PROJECT_ROW).map((summary) => ({ summary, info: infos!.get(summary.projectId)! })) : null;
    page = (
      <>
        <header className="flex h-[72px] items-center gap-[8px]">
          <div className="min-w-0 flex-1">
            <Title size={22}>Artwork</Title>
          </div>
          <Search value={search} onChange={setSearch} />
          <button type="button" aria-label="Tips" {...tip('Tips')} onClick={() => navigate('/tips')} className="glass grid size-[34px] place-items-center rounded-full text-label-2 hover:text-label">
            <Icon name="lightbulb" size={16} />
          </button>
          <Notifications />
          <WorkspaceMenu />
        </header>
        {error && !(phone && !failure && (query ? found.rows === null : recent.rows === null || recent.rows.length === 0)) ? <ErrorLine className="mt-[16px]">{error}</ErrorLine> : null}

        {query ? (
          <section className="pt-[28px]">
            {found.rows === null ? (
              found.error ? (
                phone ? <PhoneEmptyState tone="error" title="Couldn’t search your files" onRetry={found.reload} /> : null
              ) : (
                <PlaceholderGrid />
              )
            ) : found.rows.length === 0 && phone ? (
              <PhoneEmptyState tone="filtered" title={`No files match “${query}”`} line="Try another word." onClear={() => setSearch('')} />
            ) : found.rows.length === 0 ? (
              <p className="py-[48px] text-center text-[13px] text-label-2">No files match “{query}”.</p>
            ) : (
              <FileGrid>
                {found.rows.map((file) => (
                  <li key={file.id}>{card(file)}</li>
                ))}
              </FileGrid>
            )}
            <ShowMore list={found} />
          </section>
        ) : null}
        {/* Kept mounted under a search, so coming back shows the same pictures at once rather than loading them again. */}
        <div className={query ? 'hidden' : ''}>
          {/* The top is its own room: a greeting, the box, the sizes, with space round them. */}
          <div className="pb-[64px] pt-[56px]">
            <Greeting />
            <Composer brands={brands} brandId={brandId} onBrand={setBrandId} busy={busy} onStart={(start) => void create(start)} />
          </div>

          {/* Get started (lib/tours): on a computer Home marks itself, so the lessons card in the corner opens
              by itself; on a phone the New to Artwork? deck sits here, between the box and Recent. */}
          <ArtworkGetStarted on={!query} />

          <section>
            <SectionHead title="Recent" action="All Files" onAction={seeAllFiles} />
            {recent.rows === null && !recent.error ? (
              <Shelf>
                {Array.from({ length: SHELF }, (_, index) => (
                  <li key={index}>
                    <PlaceholderCard />
                  </li>
                ))}
              </Shelf>
            ) : recent.rows && recent.rows.length > 0 ? (
              <Shelf>
                {recent.rows.slice(0, RECENT).map((file) => (
                  <li key={file.id}>{card(file, { big: true, recents: true })}</li>
                ))}
              </Shelf>
            ) : phone && recent.error ? (
              <PhoneEmptyState size="sheet" tone="error" title="Couldn’t load your recent files" onRetry={recent.reload} />
            ) : phone ? (
              <PhoneEmptyState size="sheet" title="No recent files yet" line="The files you open and make show up here." />
            ) : (
              <p className="text-[13px] text-label-2">The files you open and make show here.</p>
            )}
          </section>

          {rowOfProjects === null ? (
            <section aria-hidden className="mt-[48px]">
              <ProjectsHead />
              <PlaceholderChips />
            </section>
          ) : rowOfProjects.length > 0 ? (
            <section className="mt-[48px]">
              {/* See All always: the Projects page is where each project's files fan out, not only the overflow (ART-HOM-29). */}
              <ProjectsHead count={named!.length} onSeeAll={seeAllProjects} />
              <ProjectChips projects={rowOfProjects} onOpen={openProject} />
            </section>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div className="h-dvh overflow-hidden bg-page text-label">
      {copying ? (
        <div role="status" className="glass-thick ui-pop fixed left-1/2 top-[16px] z-30 flex max-w-[calc(100%-32px)] -translate-x-1/2 items-center gap-[8px] rounded-full px-[14px] py-[7px] text-[13px] text-label">
          <Spinner size={12} className="text-label-2" />
          <span className="min-w-0 truncate">Making a copy of “{copying}”…</span>
        </div>
      ) : null}
      <div ref={scroller} className="ui-scroll h-full overflow-y-auto">
        {/* A phone's 16 px gutters, the desktop's 40 (phone direction 5, 2026-10-06). */}
        <main className="mx-auto max-w-[1180px] px-[16px] pb-[96px] sm:px-[40px]">{page}</main>
      </div>
      {picker}
      {doomed ? (
        <ConfirmAlert
          title={`Move “${doomed.name}” to Trash?`}
          message="It leaves every list, for everyone. You can restore it from Trash for 30 days; then it is deleted for good."
          action="Move to Trash"
          onConfirm={() => void remove(doomed)}
          onCancel={() => setDoomed(null)}
        />
      ) : null}
    </div>
  );
}

/** All Files on a phone with nothing in the tab: what that tab holds, in words. */
const ALL_FILES_EMPTY: Record<Below, { title: string; line: string }> = {
  mine: { title: 'No files of yours yet', line: 'Files you make show up here.' },
  shared: { title: 'Nothing shared with you yet', line: 'Files someone shares with you show up here.' },
  workspace: { title: 'No workspace files yet', line: 'Files your team makes in this workspace show up here.' },
};

/** Good morning, afternoon or evening, by the person's own clock, and their first name. */
function Greeting() {
  const { user } = useUser();
  const hour = useHour();
  const part = hour < 5 ? 'evening' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const name = user?.firstName?.trim();
  return <h2 className="mb-[20px] text-balance text-center text-[24px] font-semibold leading-[30px] tracking-[-0.015em] sm:mb-[24px] sm:text-[32px] sm:leading-[40px]">{name ? `Good ${part}, ${name}` : `Good ${part}`}</h2>;
}

/**
 * The hour on this computer's clock, kept current. Read once, a Home opened in the morning
 * said "Good morning" all afternoon, since nothing made it render again (QA 2026-10-05,
 * ART-HOM-01). Checked each minute, and again when the tab comes back into view.
 */
function useHour(): number {
  const [hour, setHour] = useState(() => new Date().getHours());
  useEffect(() => {
    const read = () => setHour(new Date().getHours());
    const timer = setInterval(read, 60_000);
    document.addEventListener('visibilitychange', read);
    window.addEventListener('focus', read);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', read);
      window.removeEventListener('focus', read);
    };
  }, []);
  return hour;
}

/** Four across: the recents and the projects. */
function Shelf({ children }: { children: ReactNode }) {
  return <ul className="grid grid-cols-2 gap-[20px] lg:grid-cols-4">{children}</ul>;
}

/** The quieter heading the projects' row sits under: they come second on the Home. */
function ProjectsHead({ count, onSeeAll }: { count?: number; onSeeAll?: () => void }) {
  return (
    <div className="mb-[14px] flex items-baseline gap-[8px]">
      <h2 className="text-[15px] font-semibold">Projects</h2>
      {count !== undefined ? <span className="text-[13px] tabular-nums text-label-3">{count}</span> : null}
      <span className="flex-1" />
      {onSeeAll ? (
        <button type="button" onClick={onSeeAll} className="text-[13px] text-accent-text hover:underline">
          See All
        </button>
      ) : null}
    </div>
  );
}

